<template>
  <div class="modal-overlay" @click.self="$emit('cancel')">
    <div class="modal-container">
      <div class="tw-flex items-center justify-between mb-4">
        <h2 class="text-xl font-semibold">{{ isEditing ? 'Edit policy version' : 'Add policy version' }}</h2>
        <button class="text-gray-400 hover:text-gray-600" @click="$emit('cancel')">
          <icon name="cross" size="16"/>
        </button>
      </div>

      <form-group label="Policy document" required>
        <dropdown
          :options="typeOptions"
          :model-value="form.type"
          @update:model-value="form.type = $event"
          :disabled="isEditing"
        />
      </form-group>

      <form-group label="Version label" required class="mt-3">
        <ds-input v-model="form.version_name" placeholder="e.g. V1.0, V2.0" maxlength="32"/>
      </form-group>

      <form-group label="Document source" required class="mt-3">
        <div class="tw-flex items-center gap-4">
          <radio-button
            name="source"
            label="External URL"
            val="link"
            :model-value="form.source"
            @update:model-value="form.source = $event"
            inline
          />
          <radio-button
            name="source"
            label="PDF upload"
            val="pdf"
            :model-value="form.source"
            @update:model-value="form.source = $event"
            inline
          />
        </div>
      </form-group>

      <div v-if="form.source === 'link'" class="mt-3">
        <form-group label="URL">
          <ds-input v-model="form.link" placeholder="https://example.com/policy"/>
        </form-group>
      </div>

      <div v-else-if="form.source === 'pdf'" class="mt-3">
        <form-group label="PDF file">
          <input
            type="file"
            accept=".pdf"
            ref="fileInput"
            @change="onFileChange"
          />
          <p v-if="form.file_name && !form.file" class="text-sm text-gray-500 mt-1">
            Current: {{ form.file_name }}
          </p>
        </form-group>
      </div>

      <div v-if="isEditing && hasMarkdown" class="mt-3">
        <p class="text-sm text-gray-500">
          This version has editable content.
          <a :href="markdownViewUrl" target="_blank" class="text-blue-600">View &amp; edit content</a>
        </p>
      </div>

      <div v-if="errors.length > 0" class="mt-3">
        <p v-for="error in errors" :key="error" class="text-sm" style="color: red;">{{ error }}</p>
      </div>

      <div class="tw-flex gap-2 mt-4">
        <ds-button
          :label="isEditing ? 'Save changes' : 'Add version'"
          variant="rounded"
          @click="submit"
          :disabled="submitting"
        />
        <ds-button label="Cancel" variant="rounded-minimal" @click="$emit('cancel')"/>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import RadioButton from '@/components/Form/RadioButton.vue'
  import * as LegalPoliciesApi from '@/Domain/Policy/Api/legal-policies'

  const TYPE_OPTIONS = [
    { value: 'terms-of-use', label: 'Terms of Use' },
    { value: 'privacy-policy', label: 'Privacy Policy' },
    { value: 'cookie-policy', label: 'Cookie Policy' },
  ]

  const TYPE_SLUG_MAP = {
    'terms-of-use': 'terms-of-use',
    'privacy-policy': 'privacy-policy',
    'cookie-policy': 'cookie-policy',
  }

  export default defineComponent({
    name: 'PolicyVersionModal',
    components: { DsButton, DsInput, Dropdown, FormGroup, Icon, RadioButton },
    props: {
      policy: {
        type: Object,
        default: null,
      },
    },
    emits: ['save', 'cancel'],
    data() {
      return {
        form: {
          type: this.policy?.type || 'terms-of-use',
          version_name: this.policy?.version_name || '',
          source: this.detectSource(),
          link: this.policy?.link || '',
          file: null,
          file_name: this.policy?.document || '',
        },
        errors: [],
        submitting: false,
      }
    },
    computed: {
      isEditing() {
        return this.policy !== null
      },
      typeOptions() {
        return TYPE_OPTIONS
      },
      hasMarkdown() {
        return this.policy?.has_markdown
      },
      markdownViewUrl() {
        if (!this.policy) return '#'
        const slug = TYPE_SLUG_MAP[this.policy.type] || this.policy.type
        return `/${slug}/${this.policy.id}`
      },
    },
    methods: {
      detectSource() {
        if (this.policy?.link) return 'link'
        if (this.policy?.document) return 'pdf'
        return 'link'
      },
      onFileChange(event) {
        const file = event.target.files[0]
        if (file) {
          this.form.file = file
        }
      },
      buildFormData() {
        const formData = new FormData()
        formData.append('type', this.form.type)
        formData.append('version_name', this.form.version_name)
        formData.append('source', this.form.source)

        if (this.form.source === 'link' && this.form.link) {
          formData.append('link', this.form.link)
        } else if (this.form.source === 'pdf' && this.form.file) {
          formData.append('file', this.form.file)
        }

        return formData
      },
      async submit() {
        this.errors = []
        this.submitting = true

        try {
          const formData = this.buildFormData()

          if (this.policy?.id) {
            await LegalPoliciesApi.update(this.policy.id, formData)
          } else {
            await LegalPoliciesApi.create(formData)
          }

          this.$emit('save')
        } catch (e) {
          if (e && e.errors) {
            this.errors = Object.values(e.errors).flat()
          } else if (e && e.message) {
            this.errors = [e.message]
          } else if (e && typeof e === 'object') {
            const values = Object.values(e).flat()
            if (values.length > 0 && typeof values[0] === 'string') {
              this.errors = values
            } else {
              this.errors = ['An unexpected error occurred.']
            }
          } else {
            this.errors = ['An unexpected error occurred.']
          }
        }

        this.submitting = false
      },
    },
  })
</script>

<style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    width: 500px;
    max-width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  }

  :deep(.radio--inline) {
    align-items: center;
  }
</style>
